import axios from "axios";
import { portalFor, getToken, getRefreshToken, setToken, clearToken, loginPathFor } from "./session";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// ---- short-lived GET cache: avoids re-fetching the same data on every tab switch; cleared by any write ----
const GET_CACHE = new Map();
const CACHE_TTL = 25000;
const baseAdapter = axios.getAdapter(axios.defaults.adapter);
api.defaults.adapter = async (cfg) => {
  const cacheable = cfg.method === "get" && cfg.cache !== false && !cfg.responseType;
  const key = cacheable ? `${cfg.portal}|${cfg.url}|${JSON.stringify(cfg.params || {})}` : null;
  if (cacheable) {
    const hit = GET_CACHE.get(key);
    if (hit && Date.now() - hit.t < CACHE_TTL) return { ...hit.res, config: cfg, cached: true };
  }
  const res = await baseAdapter(cfg);
  if (cacheable) GET_CACHE.set(key, { t: Date.now(), res });
  else if (cfg.method !== "get") GET_CACHE.clear();
  return res;
};
export const clearApiCache = () => GET_CACHE.clear();

api.interceptors.request.use((cfg) => {
  const portal = cfg.portal || portalFor();
  const token = getToken(portal);
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  cfg.portal = portal;
  return cfg;
});

const AUTH_ENDPOINTS = ["/auth/login", "/teacher/login", "/auth/signup-otp", "/auth/verify-otp", "/auth/forgot-password", "/auth/reset-password", "/auth/refresh"];

// Single-flight silent refresh per portal (rolling 30-day session).
const refreshing = {};
async function refreshSession(portal) {
  if (!refreshing[portal]) {
    const rt = getRefreshToken(portal);
    refreshing[portal] = (rt ? axios.post(`${API}/auth/refresh`, { refresh_token: rt }) : Promise.reject(new Error("no refresh token")))
      .then((r) => { setToken(portal, r.data.token, r.data.refresh_token); return r.data.token; })
      .finally(() => { delete refreshing[portal]; });
  }
  return refreshing[portal];
}

function endSession(portal, detail) {
  clearToken(portal);
  const loginPath = loginPathFor(portal);
  if (!window.location.pathname.startsWith(loginPath)) {
    const reason = detail === "Access revoked" ? "revoked" : "expired";
    window.location.assign(`${loginPath}?reason=${reason}`);
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err.response?.status;
    const detail = err.response?.data?.detail;
    const url = err.config?.url || "";
    const portal = err.config?.portal || portalFor();
    const isAuthCall = AUTH_ENDPOINTS.some((p) => url.startsWith(p));
    if (status === 401 && !isAuthCall && !err.config.__retried && getRefreshToken(portal)) {
      const usedToken = (err.config.headers?.Authorization || "").replace("Bearer ", "");
      try {
        const token = await refreshSession(portal);
        err.config.__retried = true;
        err.config.headers.Authorization = `Bearer ${token}`;
        return api.request(err.config);
      } catch (e) {
        // Another tab may have already rotated the session: reuse its token instead of logging out.
        const latest = getToken(portal);
        if (latest && latest !== usedToken) {
          err.config.__retried = true;
          err.config.headers.Authorization = `Bearer ${latest}`;
          return api.request(err.config);
        }
        err.__sessionExpired = true;
        endSession(portal, detail);
        return Promise.reject(err);
      }
    }
    const sessionDead = status === 401 || (status === 403 && detail === "Access revoked");
    if (sessionDead && !isAuthCall) {
      err.__sessionExpired = true;
      endSession(portal, detail);
    }
    if (err && typeof err === "object") err.__isAxiosError = true;
    return Promise.reject(err);
  }
);

// Human-readable reason for any failed request (used by toasts and page error states).
export function errMsg(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  if (!err.response) return err.code === "ECONNABORTED" ? "The request timed out. Please try again." : "Cannot reach the server. Check your internet connection.";
  const { status, data } = err.response;
  let detail = data?.detail;
  if (Array.isArray(detail)) detail = detail.map((d) => d?.msg || JSON.stringify(d)).join(" ");
  if (typeof detail === "string" && detail.startsWith("PLAN_UPGRADE_REQUIRED")) return "This feature is not included in your current plan. Upgrade to unlock it.";
  if (typeof detail === "string" && detail.startsWith("PLAN_LIMIT_REACHED:")) return detail.split(":").slice(2).join(":");
  if (status === 402) return detail || "Your account is read-only until the pending payment is completed.";
  if (status === 401) {
    const authCall = AUTH_ENDPOINTS.some((p) => (err.config?.url || "").startsWith(p));
    return authCall && typeof detail === "string" ? detail : "Session expired, please log in again.";
  }
  if (status === 403) return detail || "You do not have access to this page.";
  if (status === 404) return detail || "The item you are looking for was not found.";
  if (status === 429) return detail || "Too many attempts. Please wait a minute and try again.";
  if (status >= 500) return "Something went wrong on our side. Please try again in a moment.";
  return detail || fallback;
}

if (typeof window !== "undefined" && !window.__cdAxiosGuardInstalled) {
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (reason && (reason.__isAxiosError || reason.isAxiosError || reason?.config?.baseURL?.startsWith?.(API))) {
      // eslint-disable-next-line no-console
      console.warn("[axios:unhandled]", reason.response?.status, reason.config?.url, reason.message);
      event.preventDefault();
    }
  });
  window.__cdAxiosGuardInstalled = true;
}

export const formatINR = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
